// Shares one request between every caller that asks for the same thing.
//
// The product API's ingress rate-limits each client, so the same body must not
// go over the wire more than once: several blocks asking for the same filter,
// an effect re-running, a refetch on window focus or a re-mounted component
// all resolve to one network request. The in-flight promise is shared, and a
// successful result is kept for `ttlMs` so a repeat right after it is free.
// Failures are never cached — the next caller retries.

interface Entry {
  promise: Promise<unknown>;
  expiresAt: number;
}

// Cap so a long-lived tab that browses many filter combinations can't grow the
// map without bound. Oldest entries go first (Map keeps insertion order).
const MAX_ENTRIES = 100;

const store = new Map<string, Entry>();

// JSON.stringify with object keys sorted, so two bodies that differ only in key
// order produce the same cache key. Array order is kept as given.
const sortKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    return Object.keys(source)
      .sort()
      .reduce<Record<string, unknown>>((sorted, key) => {
        sorted[key] = sortKeys(source[key]);
        return sorted;
      }, {});
  }
  return value;
};

export const requestKey = (scope: string, body?: unknown): string =>
  body === undefined ? scope : `${scope}:${JSON.stringify(sortKeys(body))}`;

const evict = (now: number) => {
  store.forEach((entry, key) => {
    if (entry.expiresAt <= now) store.delete(key);
  });
  while (store.size >= MAX_ENTRIES) {
    const oldest = store.keys().next().value;
    if (oldest === undefined) break;
    store.delete(oldest);
  }
};

export const cachedRequest = <T>(
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>
): Promise<T> => {
  const now = Date.now();
  const hit = store.get(key);
  if (hit && hit.expiresAt > now) return hit.promise as Promise<T>;

  evict(now);

  // While in flight the entry lives for `ttlMs` too, so a request that never
  // settles can't pin every later caller to it.
  const entry: Entry = { promise: fetcher(), expiresAt: now + ttlMs };
  store.set(key, entry);

  entry.promise.then(
    () => {
      entry.expiresAt = Date.now() + ttlMs;
    },
    () => {
      if (store.get(key) === entry) store.delete(key);
    }
  );

  return entry.promise as Promise<T>;
};
